import React, { useState } from 'react';
import type { ActivityViewMode } from '../../core/types/activityViewMode.types';
import type {
  ExploraPayload,
  ExploraProjection,
  ExploraRuntime,
  ExploraSkin,
  ExploraVariable,
} from '../../core/types/payloads/sandbox.types';
import { formatParts } from './fractionUtils';
import { BalanzaSvgViewport } from './BalanzaSvgViewport';
import { EconomyChartViewport } from './EconomyChartViewport';
import { EXPLORA_SKIN_PRESETS, resolveSkin } from './exploraSkins';
import { isInverseOperationsPayload } from './balanzaEquation';

function StatusBadge({ status, inverseMode }: { status: ExploraRuntime['status']; inverseMode?: boolean }) {
  if (status === 'SUCCESS') {
    return <span className="explora-chip explora-chip--success">{inverseMode ? '¡Despejado!' : 'Objetivo ✓'}</span>;
  }
  if (status === 'FAILED') {
    return <span className="explora-chip explora-chip--warn">Finalizada</span>;
  }
  if (status === 'PLAYING') {
    return <span className="explora-chip">En juego</span>;
  }
  return null;
}

function SkinMenu({ active, onSelect }: { active: ExploraSkin; onSelect: (s: ExploraSkin) => void }) {
  const [open, setOpen] = useState(false);
  const preset = EXPLORA_SKIN_PRESETS[active];
  return (
    <div className="relative">
      <button type="button" className="explora-toolbar-btn" onClick={() => setOpen((o) => !o)}>
        {preset.icon} Máscara ▾
      </button>
      {open && (
        <>
          <button type="button" className="fixed inset-0 z-10" aria-label="Cerrar" onClick={() => setOpen(false)} />
          <div className="explora-popover z-20">
            {(Object.keys(EXPLORA_SKIN_PRESETS) as ExploraSkin[]).map((id) => {
              const p = EXPLORA_SKIN_PRESETS[id];
              return (
                <button
                  key={id}
                  type="button"
                  className="explora-popover-item"
                  data-active={id === active}
                  onClick={() => {
                    onSelect(id);
                    setOpen(false);
                  }}
                >
                  {p.icon} {p.label}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function VariablesSidebar({
  variables,
  collapsed,
  onToggle,
  onAdd,
  onUpdate,
  onRemove,
}: {
  variables: ExploraVariable[];
  collapsed: boolean;
  onToggle: () => void;
  onAdd?: () => void;
  onUpdate?: (index: number, patch: Partial<ExploraVariable>) => void;
  onRemove?: (index: number) => void;
}) {
  if (collapsed) {
    return (
      <aside className="explora-sidebar explora-sidebar--collapsed">
        <button type="button" className="explora-sidebar-toggle" onClick={onToggle} title="Acciones alumno">
          📱
        </button>
      </aside>
    );
  }

  return (
    <aside className="explora-sidebar">
      <div className="explora-sidebar-head">
        <span className="explora-sidebar-title">Acciones alumno</span>
        <button type="button" className="explora-sidebar-toggle" onClick={onToggle} aria-label="Colapsar">
          −
        </button>
      </div>
      <div className="explora-sidebar-scroll">
        {variables.map((v, i) => (
          <div key={v.id} className="explora-var-row">
            <input
              className="canvas-inline-input w-full bg-transparent text-xs font-bold"
              value={v.label}
              onChange={(e) => onUpdate?.(i, { label: e.target.value })}
            />
            <div className="flex items-center gap-1.5">
              <input
                type="range"
                min={v.type === 'MULTIPLY' ? 0.1 : -20}
                max={v.type === 'MULTIPLY' ? 3 : 20}
                step={v.type === 'MULTIPLY' ? 0.05 : 1}
                value={v.internalValue}
                onChange={(e) => onUpdate?.(i, { internalValue: Number(e.target.value) })}
                className="min-w-0 flex-1 accent-[var(--sala-accent-neon,#38bdf8)]"
              />
              <span className="w-8 shrink-0 text-right font-mono text-[10px] tabular-nums opacity-70">
                {v.internalValue}
              </span>
              <button type="button" className="text-[10px] text-red-400" onClick={() => onRemove?.(i)}>
                ✕
              </button>
            </div>
          </div>
        ))}
        <button type="button" className="explora-toolbar-btn w-full text-xs" onClick={onAdd}>
          + Acción
        </button>
      </div>
    </aside>
  );
}

export interface ExploraLiveStageProps {
  payload: ExploraPayload;
  runtime: ExploraRuntime;
  projection?: ExploraProjection;
  mode?: ActivityViewMode;
  size?: 'md' | 'lg';
  badge?: string;
  fill?: boolean;
  children?: React.ReactNode;
  onPayloadChange?: (patch: Partial<ExploraPayload>) => void;
  onVariableChange?: (index: number, patch: Partial<ExploraVariable>) => void;
  onAddVariable?: () => void;
  onRemoveVariable?: (index: number) => void;
  onSkinChange?: (skin: ExploraSkin) => void;
  dispatch?: (action: { type: string; payload?: unknown }) => void;
}

export function ExploraLiveStage({
  payload,
  runtime,
  projection,
  mode = 'projector',
  size = 'md',
  badge,
  fill = false,
  children,
  onPayloadChange,
  onVariableChange,
  onAddVariable,
  onRemoveVariable,
  onSkinChange,
  dispatch,
}: ExploraLiveStageProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const maxValue = projection?.maxValue ?? payload.maxValue ?? Math.max(payload.targetValue * 2, 20);
  const skin = resolveSkin(payload);
  const isEditor = mode === 'editor';
  const inverseMode = isInverseOperationsPayload(payload);
  const useEconomyChart = payload.variant === 'grafico' || skin === 'economy';
  const showSidebar = isEditor && payload.variant !== 'fracciones';

  const proj: ExploraProjection =
    projection ?? {
      variant: payload.variant,
      title: payload.title,
      currentValue: runtime.currentValue,
      targetValue: payload.targetValue,
      initialValue: payload.initialValue,
      maxValue,
      difference: runtime.currentValue - payload.targetValue,
      status: runtime.status,
      displayEquation: payload.displayEquation,
      unitLabel: payload.unitLabel,
      history: runtime.history,
      currentDeck: runtime.currentDeck,
      blockLabels: runtime.history.map((h) => h.variableLabel),
      currentFractionLabel: formatParts(runtime.currentValue, maxValue),
      targetFractionLabel: formatParts(payload.targetValue, maxValue),
    };

  const previewCurrent = isEditor ? payload.initialValue : proj.currentValue;
  const liveEquation = proj.liveEquation ?? proj.displayEquation ?? payload.displayEquation;
  const equationLeft = proj.equationLeft ?? payload.equationLeft;
  const equationRight = proj.equationRight ?? payload.equationRight;
  const hint =
    inverseMode
      ? 'Despeja la incógnita'
      : proj.difference === 0
        ? 'Equilibrio'
        : proj.difference < 0
          ? `−${Math.abs(proj.difference)} ${proj.unitLabel ?? ''}`
          : `+${proj.difference} ${proj.unitLabel ?? ''}`;

  return (
    <div
      className={`explora-live-stage ${fill ? 'explora-live-stage--fill' : ''}`}
      data-mode={mode}
      style={{
        borderColor: runtime.status === 'SUCCESS' && !isEditor ? '#22c55e55' : undefined,
      }}
    >
      {/* Toolbar compacta */}
      <header className="explora-toolbar">
        <div className="explora-toolbar-start min-w-0 flex-1">
          {isEditor ? (
            <input
              className="canvas-inline-input canvas-inline-input--title min-w-0 flex-1 bg-transparent font-display text-base font-bold sm:text-lg"
              value={payload.title}
              onChange={(e) => onPayloadChange?.({ title: e.target.value })}
              placeholder="Título"
            />
          ) : (
            <>
              {badge && <span className="explora-chip shrink-0">{badge}</span>}
              <StatusBadge status={runtime.status} inverseMode={inverseMode} />
              <h3 className="min-w-0 truncate font-display text-sm font-bold sm:text-base">{payload.title}</h3>
            </>
          )}
        </div>
        <div className="explora-toolbar-end shrink-0">
          {isEditor && onSkinChange && <SkinMenu active={skin} onSelect={onSkinChange} />}
          {!isEditor && liveEquation && (
            <span className="explora-chip hidden sm:inline">{liveEquation}</span>
          )}
          {!isEditor && mode !== 'student' && (
            <span className="explora-chip explora-chip--muted">{hint}</span>
          )}
        </div>
      </header>

      {isEditor && (
        <div className="explora-subtoolbar">
          <input
            className="canvas-inline-input min-w-0 flex-1 bg-transparent text-xs"
            value={payload.displayEquation ?? ''}
            placeholder="Ecuación (ej. 2x + y = 10)"
            onChange={(e) => onPayloadChange?.({ displayEquation: e.target.value || undefined })}
          />
        </div>
      )}

      {/* Cuerpo: lienzo + sidebar */}
      <div className="explora-body">
        <div className="explora-canvas-wrap">
          <div className="explora-canvas-main">
            {useEconomyChart && !inverseMode ? (
              <EconomyChartViewport
                currentValue={previewCurrent}
                targetValue={payload.targetValue}
                initialValue={payload.initialValue}
                unitLabel={payload.unitLabel}
                productLabel={payload.title}
                history={isEditor ? [] : runtime.history}
                variables={payload.availableVariables}
                balanced={previewCurrent === payload.targetValue}
                size={size}
                mode={mode}
                fill={fill}
              />
            ) : payload.variant === 'balanza' || payload.variant === 'grafico' ? (
              <BalanzaSvgViewport
              currentValue={previewCurrent}
              targetValue={inverseMode ? (equationRight?.constants ?? payload.targetValue) : payload.targetValue}
              difference={inverseMode ? 0 : previewCurrent - payload.targetValue}
              unitLabel={payload.unitLabel}
              displayEquation={isEditor ? undefined : liveEquation}
              blockLabels={isEditor ? [] : proj.blockLabels}
              balanced={inverseMode ? true : previewCurrent === payload.targetValue}
              inverseMode={inverseMode}
              equationLeft={equationLeft}
              equationRight={equationRight}
              animationGeneration={runtime.history.length}
              size={size}
              mode={mode}
              skin={skin}
              fill={fill}
              onTargetChange={isEditor && !inverseMode ? (targetValue) => onPayloadChange?.({ targetValue }) : undefined}
            />
          ) : (
            <FraccionesProjector projection={proj} size={size} compact={fill} />
          )}
          </div>

          {children && <div className="explora-context-bar">{children}</div>}

          {mode === 'student' && dispatch && (
            <div className="explora-student-bar">
              <StudentControls
                payload={payload}
                runtime={runtime}
                projection={projection}
                dispatch={dispatch}
              />
            </div>
          )}
        </div>

        {showSidebar && (
          <VariablesSidebar
            variables={payload.availableVariables}
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed((c) => !c)}
            onAdd={onAddVariable}
            onUpdate={onVariableChange}
            onRemove={onRemoveVariable}
          />
        )}
      </div>
    </div>
  );
}

function StudentControls({
  payload,
  runtime,
  projection,
  dispatch,
}: {
  payload: ExploraPayload;
  runtime: ExploraRuntime;
  projection?: ExploraProjection;
  dispatch: (action: { type: string; payload?: unknown }) => void;
}) {
  const disabled = runtime.status !== 'PLAYING';
  const deck = projection?.currentDeck ?? runtime.currentDeck;
  const options = payload.deckMode && deck?.length ? deck : payload.availableVariables;

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {options.map((variable) => (
        <button
          key={variable.id}
          type="button"
          disabled={disabled}
          className="explora-action-btn disabled:opacity-40"
          onClick={() => dispatch({ type: 'APPLY_VARIABLE', payload: { variableId: variable.id } })}
        >
          {variable.label}
        </button>
      ))}
    </div>
  );
}

export function FraccionesProjector({
  projection,
  size = 'md',
  compact = false,
}: {
  projection: ExploraProjection;
  size?: 'md' | 'lg';
  compact?: boolean;
}) {
  const { currentValue, targetValue, maxValue, status, currentFractionLabel, targetFractionLabel } = projection;
  const pct = maxValue > 0 ? (currentValue / maxValue) * 100 : 0;
  const targetPct = maxValue > 0 ? (targetValue / maxValue) * 100 : 0;

  return (
    <div className={`mx-auto w-full ${compact ? 'h-full flex flex-col justify-center' : 'max-w-3xl'}`}>
      <div className={`flex justify-center gap-6 text-center ${compact ? 'mb-2' : 'mb-4'}`}>
        <div>
          <p className="text-[10px] font-bold uppercase opacity-60">Actual</p>
          <p className={`font-display font-extrabold ${size === 'lg' && !compact ? 'text-5xl' : 'text-2xl'}`}>
            {currentFractionLabel}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase text-red-400">Meta</p>
          <p className={`font-display font-extrabold text-red-400 ${size === 'lg' && !compact ? 'text-4xl' : 'text-xl'}`}>
            {targetFractionLabel}
          </p>
        </div>
      </div>
      <div className={`relative overflow-hidden rounded-xl border-2 ${compact ? 'h-10' : 'h-14'}`} style={{ borderColor: 'var(--plugin-border)' }}>
        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-sky-400 to-violet-500 transition-all duration-700" style={{ width: `${pct}%` }} />
        <div className="absolute inset-y-0 z-10 w-0.5 bg-red-500" style={{ left: `${targetPct}%` }} />
      </div>
      {status === 'SUCCESS' && !compact && (
        <p className="mt-3 text-center text-sm font-bold text-emerald-400">¡Meta alcanzada!</p>
      )}
    </div>
  );
}

export function ExploraShell(
  props: Omit<ExploraLiveStageProps, 'mode' | 'fill'> & { children?: React.ReactNode },
) {
  const { children, ...rest } = props;
  return (
    <ExploraLiveStage {...rest} mode="host" fill>
      {children}
    </ExploraLiveStage>
  );
}

export const TargetReachShell = ExploraShell;

export function ExploraStudentControls(props: {
  payload: ExploraPayload;
  runtime: ExploraRuntime;
  projection?: ExploraProjection;
  dispatch: (action: { type: string; payload?: unknown }) => void;
}) {
  return (
    <StudentControls
      payload={props.payload}
      runtime={props.runtime}
      projection={props.projection}
      dispatch={props.dispatch}
    />
  );
}

export const TargetReachStudentControls = ExploraStudentControls;

export function BalanzaProjector({ projection, size = 'md' }: { projection: ExploraProjection; size?: 'md' | 'lg' }) {
  return (
    <BalanzaSvgViewport
      currentValue={projection.currentValue}
      targetValue={projection.targetValue}
      difference={projection.difference}
      unitLabel={projection.unitLabel}
      displayEquation={projection.liveEquation ?? projection.displayEquation}
      blockLabels={projection.blockLabels}
      balanced={projection.inverseMode ? true : projection.difference === 0}
      inverseMode={projection.inverseMode}
      equationLeft={projection.equationLeft}
      equationRight={projection.equationRight}
      animationGeneration={projection.history.length}
      size={size}
      mode="projector"
      fill
    />
  );
}
